import { Controller } from "@hotwired/stimulus"

// top-nav--search — debounced auto-submit for the global search modal.
// The form is a plain GET (so JS-disabled operators can still submit with
// Enter); this shaves time-to-results when JS is on. Mirrors the
// docs--search controller, targeting the results Turbo Frame declared by
// the form's data-turbo-frame so only the results list re-renders.
//
// MC-1896: `closeModal` closes the search modal when a result is clicked,
// through a `core--modal` outlet. It calls the outlet's `close()` with no
// event on purpose: `core--modal#close` preventDefault()s any event it is
// handed, which would cancel the result link's own `_top` navigation. The
// modal is therefore closed before Turbo snapshots the page, so it is neither
// left open over the destination nor restored by a Back navigation.
//
// MC-1898: drives the results from the keyboard, after the
// `core--multi-select` pattern — ArrowDown/ArrowUp move a wrapping
// highlight across every option in both sections, and Enter clicks the
// highlighted row's own link, so it takes exactly the mouse's path. Unlike
// multi-select, focus stays in the input, so the input's
// `aria-activedescendant` names the active option. The frame is replaced on
// every debounced keystroke, so the highlight is kept as the option's
// stable id (not a position or an element), options are read fresh on each
// keypress, and `restoreHighlight` re-applies it after each frame render.
// Escape is left to `core--modal`; Enter with nothing highlighted still
// submits the form.
export default class extends Controller {
  static targets = ["form", "input"]

  static outlets = ["core--modal"]

  static values = { delay: { type: Number, default: 300 } }

  initialize() {
    this.timer = null
    this.highlightedId = null
  }

  queue() {
    if (this.timer) {
      clearTimeout(this.timer)
    }
    this.timer = setTimeout(() => {
      if (this.hasFormTarget) this.formTarget.requestSubmit()
    }, this.delayValue)
  }

  closeModal() {
    if (this.hasCoreModalOutlet) this.coreModalOutlet.close()
  }

  navigate(event) {
    if (event.isComposing) return
    if (event.altKey || event.ctrlKey || event.metaKey) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveHighlight(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveHighlight(-1)
        break
      case "Enter": {
        const current = this.highlightedOption()
        if (!current) return
        event.preventDefault()
        if (this.timer) clearTimeout(this.timer)
        current.click()
        break
      }
    }
  }

  moveHighlight(delta) {
    const options = this.options()
    if (options.length === 0) return
    let index = options.indexOf(this.highlightedOption()) + delta
    if (index < 0) index = options.length - 1
    if (index >= options.length) index = 0
    this.setHighlight(options[index])
  }

  setHighlight(current) {
    this.highlightedId = current ? current.id : null
    this.options().forEach((option) => {
      option.classList.remove("is-highlighted")
      option.setAttribute("aria-selected", "false")
    })
    if (current) {
      current.classList.add("is-highlighted")
      current.setAttribute("aria-selected", "true")
      current.scrollIntoView({ block: "nearest" })
      this.inputTarget.setAttribute("aria-activedescendant", current.id)
    } else {
      this.inputTarget.removeAttribute("aria-activedescendant")
    }
  }

  // After the results frame re-renders, re-point the highlight at the row
  // with the same id; if that row is gone, clear it rather than point at
  // nothing.
  restoreHighlight() {
    if (!this.hasInputTarget) return
    this.inputTarget.setAttribute(
      "aria-expanded",
      String(this.options().length > 0)
    )
    this.setHighlight(this.highlightedOption())
  }

  highlightedOption() {
    if (this.highlightedId === null) return null
    return this.options().find((option) => option.id === this.highlightedId) || null
  }

  options() {
    const listbox = document.getElementById(
      this.inputTarget.getAttribute("aria-controls")
    )
    return listbox ? Array.from(listbox.querySelectorAll('[role="option"]')) : []
  }

  disconnect() {
    if (this.timer) clearTimeout(this.timer)
  }
}
